import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import config from '../../config';
import { store } from '../../store';
import { retriggerRequest } from '../../services/api/offline-consult';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';

const BRAND = '#6C5DD3';
const BRAND_LIGHT = '#F5F4FF';
const BRAND_BORDER = '#E5E9FE';
const TEXT_PRIMARY = '#0F0B28';
const TEXT_SECONDARY = '#585969';
const TEXT_MUTED = '#A5A6BB';
const BORDER = '#E3E3E3';
const DANGER = '#EB5757';
const DANGER_LIGHT = '#FFF0F0';

const CHANNEL_META = {
  email:    { icon: '✉️', label: 'Email',    bg: '#EFF6FF', color: '#0049C6' },
  sms:      { icon: '💬', label: 'SMS',      bg: '#EDFAF4', color: '#1eb865' },
  whatsapp: { icon: '📱', label: 'WhatsApp', bg: '#EDFAF4', color: '#25D366' },
};

function ChannelBadge({ channel }) {
  const meta = CHANNEL_META[channel] || { icon: '📩', label: channel, bg: BRAND_LIGHT, color: BRAND };
  return (
    <span style={{ background: meta.bg, color: meta.color, fontSize: '11px', fontWeight: 600, borderRadius: '4px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {meta.icon} {meta.label}
    </span>
  );
}

function ShowPharmacyCommsHistory({ sourceId }) {
  const [responseData, setResponseData] = useState(null);
  const [canViewCommsHistory, setCanViewCommsHistory] = useState(true);
  const [selectedComm, setSelectedComm] = useState(null);
  const [commToRetrigger, setCommToRetrigger] = useState(null);
  const [showRetriggerModal, setShowRetriggerModal] = useState(false);

  useEffect(() => {
    if (!sourceId) return;
    const fetchHistory = async () => {
      try {
        const state = store.getState();
        const headers = state.user.token ? { authorization: state.user.token } : {};
        const { data } = await axios.get(
          `${config.pharmacyService}/v2/ops/get-comms-history?sourceId=${sourceId}`,
          { headers }
        );
        setResponseData(data);
      } catch (err) {
        const statusCode = err?.response?.status;
        const msg = String(err?.response?.data?.message || '').toLowerCase();
        const isAccessError = statusCode === 401 || statusCode === 403 || msg.includes('access denied') || msg.includes('unauthorized');
        if (isAccessError) setCanViewCommsHistory(false);
      }
    };
    fetchHistory();
  }, [sourceId]);

  const tableData = useMemo(() => {
    if (!responseData?.commsLogs) return [];
    const alreadyExists = selectedComm
      ? responseData.commsLogs.some(c => c.commsContentId === selectedComm.commsContentId)
      : true;
    return alreadyExists ? responseData.commsLogs : [selectedComm, ...responseData.commsLogs];
  }, [responseData, selectedComm]);

  const closeRetriggerModal = () => {
    setShowRetriggerModal(false);
    setCommToRetrigger(null);
  };

  const handleRetrigger = async (eventType) => {
    try {
      const res = await retriggerRequest(sourceId, eventType);
      if (res.message === 'success') {
        toast.success('Retriggered successfully');
        closeRetriggerModal();
      } else {
        toast.error('Something went wrong');
      }
    } catch {
      toast.error('Something went wrong');
    }
  };

  if (!canViewCommsHistory) return null;

  const thStyle = {
    padding: '9px 14px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: TEXT_SECONDARY,
    background: BRAND_LIGHT,
    borderBottom: `1px solid ${BRAND_BORDER}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

        {/* Table */}
        <div style={{ flex: selectedComm ? '0 0 60%' : '1', minWidth: 0 }}>
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: '6px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {['Content ID', 'Event', 'User Type', 'Triggered At', 'Channel', 'Actions'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: TEXT_MUTED, fontSize: '13px' }}>
                      No communication history found
                    </td>
                  </tr>
                ) : tableData.map((row, i) => {
                  const isSelected = selectedComm?.commsContentId === row.commsContentId;
                  return (
                    <tr key={i} style={{ background: isSelected ? BRAND_LIGHT : i % 2 === 0 ? '#fff' : '#FAFAFA', borderBottom: `1px solid #F5F5F5` }}>
                      <td style={{ padding: '9px 14px', color: TEXT_MUTED, fontWeight: 600, fontSize: '12px' }}>{row.commsContentId}</td>
                      <td style={{ padding: '9px 14px', color: TEXT_PRIMARY, fontWeight: 600 }}>{row.eventType}</td>
                      <td style={{ padding: '9px 14px', color: TEXT_SECONDARY }}>{row.userType}</td>
                      <td style={{ padding: '9px 14px', color: TEXT_SECONDARY, fontSize: '12px' }}>{row.triggeredAt}</td>
                      <td style={{ padding: '9px 14px' }}><ChannelBadge channel={row.channel} /></td>
                      <td style={{ padding: '9px 14px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => setSelectedComm(isSelected ? null : row)}
                            style={{ height: '28px', padding: '0 12px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${isSelected ? BRAND_BORDER : BORDER}`, background: isSelected ? BRAND_LIGHT : '#fff', color: isSelected ? BRAND : TEXT_SECONDARY, cursor: 'pointer' }}
                          >
                            {isSelected ? 'Close' : 'View'}
                          </button>
                          <button
                            onClick={() => { setCommToRetrigger(row); setShowRetriggerModal(true); }}
                            style={{ height: '28px', padding: '0 12px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${DANGER_LIGHT}`, background: DANGER_LIGHT, color: DANGER, cursor: 'pointer' }}
                          >
                            Retrigger
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Panel */}
        {selectedComm && (
          <div style={{ flex: '0 0 38%', border: `1px solid ${BORDER}`, borderRadius: '6px', background: '#fff', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${BORDER}`, background: BRAND_LIGHT }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ChannelBadge channel={selectedComm.channel} />
                <span style={{ fontSize: '12px', color: TEXT_MUTED }}>ID: {selectedComm.commsContentId}</span>
              </div>
              <button onClick={() => setSelectedComm(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTED, fontSize: '16px', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '14px' }}>
              <div style={{ fontSize: '11px', color: TEXT_MUTED, marginBottom: '10px' }}>{selectedComm.triggeredAt}</div>
              {selectedComm.channel === 'email' ? (
                <iframe
                  title="Email Content"
                  srcDoc={selectedComm.emailContent}
                  style={{ width: '100%', minHeight: '380px', border: `1px solid ${BORDER}`, borderRadius: '4px', background: '#fff' }}
                />
              ) : (
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: TEXT_SECONDARY, lineHeight: '1.6', background: '#FAFAFA', border: `1px solid ${BORDER}`, borderRadius: '4px', padding: '12px' }}>
                  {selectedComm.channel === 'whatsapp' ? selectedComm.whatsappContent : selectedComm.smsContent}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Retrigger Modal */}
      <Modal isOpen={showRetriggerModal} toggle={closeRetriggerModal} centered>
        <ModalHeader toggle={closeRetriggerModal} style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: '12px' }}>
          <span style={{ fontSize: '15px', fontWeight: 700, color: TEXT_PRIMARY }}>Confirm Retrigger</span>
        </ModalHeader>
        <ModalBody style={{ padding: '20px 24px', fontSize: '13px', color: TEXT_SECONDARY }}>
          Are you sure you want to retrigger the <strong style={{ color: TEXT_PRIMARY }}>{commToRetrigger?.eventType}</strong> communication for this order?
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${BORDER}`, gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={closeRetriggerModal}
            style={{ height: '34px', padding: '0 18px', fontSize: '13px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${BORDER}`, background: '#fff', color: TEXT_SECONDARY, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={() => handleRetrigger(commToRetrigger?.eventType)}
            style={{ height: '34px', padding: '0 18px', fontSize: '13px', fontWeight: 600, borderRadius: '4px', border: `1px solid ${BRAND}`, background: BRAND, color: '#fff', cursor: 'pointer' }}
          >
            Retrigger
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => ({ authToken: state.user.authToken, user: state.user });
export default connect(mapStateToProps)(ShowPharmacyCommsHistory);
